import { CommonModule, DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ChipModule } from 'primeng/chip';
import { DatePickerModule } from 'primeng/datepicker';
import { DividerModule } from 'primeng/divider';
import { PanelModule } from 'primeng/panel';
import { PopoverModule } from 'primeng/popover';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { firstValueFrom, Subject, Subscription } from 'rxjs';
import { CardpageBaseComponent } from '../../abstract/cardpage-base/cardpage-base.component';
import { TrendIndicatorComponent } from '../../components/trend-indicator/trend-indicator.component';
import { TrendComponent } from '../../components/trend/trend.component';
import { AlertDialogComponent } from '../../dialogs/alert-dialog/alert-dialog.component';
import { BreathSessionDialogComponent } from '../../dialogs/breath-session-dialog/breath-session-dialog.component';
import { SynopticTableDialogComponent } from '../../dialogs/synoptic-table-dialog/synoptic-table-dialog.component';
import { Alert, BreathSession, PatientReport, SummaryPatient } from '../../models/summary';
import { ApiService } from '../../services/api.service';
import { UiNotificationService } from '../../services/ui-notification.service';
import { DateOnlyPipe, DurationPipe, StatusDatePipe } from '../../utilities/date-pipes';
import { Parameters } from '../../utilities/parameters';
import { Reset } from '../../utilities/reset';
import { HearthSignalsDialogComponent } from '../../dialogs/hearth-signals-dialog/hearth-signals-dialog.component';
import { AccSignalsDialogComponent } from '../../dialogs/acc-signals-dialog/acc-signals-dialog.component';
import { BreathSignalsDialogComponent } from '../../dialogs/breath-signals-dialog/breath-signals-dialog.component';
import { FloatLabelModule } from 'primeng/floatlabel';

@Component({
  selector: 'app-summary-patient',
  imports: [
    CommonModule,
    FormsModule,
    TranslatePipe,
    CardpageBaseComponent,
    CardModule,
    StatusDatePipe,
    DateOnlyPipe,
    ButtonModule,
    PanelModule,
    DividerModule,
    TrendIndicatorComponent,
    DurationPipe,
    TabsModule,
    SelectButtonModule,
    PopoverModule,
    DatePickerModule,
    ChipModule,
    TrendComponent,
    TableModule,
    TranslatePipe,
    DatePipe,
    AlertDialogComponent,
    DurationPipe,
    BreathSessionDialogComponent,
    PanelModule,
    SynopticTableDialogComponent,
    AccSignalsDialogComponent,
    BreathSignalsDialogComponent,
    HearthSignalsDialogComponent,
    FloatLabelModule
  ],
  templateUrl: './summary-patient.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: ``,
})
export class SummaryPatientComponent implements OnInit, OnDestroy {
  private apiService = inject(ApiService);
  messageService = inject(UiNotificationService);
  private routerService = inject(Router);
  private translate = inject(TranslateService);
  private langSubscription!: Subscription

  loading: boolean = false;
  loadingMetrics: boolean = false;
  dtTrigger: Subject<boolean> = new Subject<boolean>();

  patient: SummaryPatient = Reset.summaryPatient();
  reports: PatientReport[] = [];
  alerts: Alert[] = [];
  breathSessions: BreathSession[] = [];

  timeOptions: any[] = [];
  selectedTime:number = 2;
  customDateRange: Date[] | undefined;

  private id: string = '';
  selectedId: string = '';

  tabsId: string = '0';
  max: number = 0;
  min: number = 0;

  alertsToAnalyse: number = 0;

  zoomLevel: Subject<any> = new Subject<any>();
  zoomCommand: Subject<number> = new Subject<number>();

  hasBreath:boolean = Parameters.loggedUser.features.breath;
  hasGait: boolean = Parameters.loggedUser.features.gait;
  hasPosture: boolean = Parameters.loggedUser.features.posture;

  selectedAlert: Alert = Reset.alert();
  selectedBreathSession: BreathSession = Reset.breathSession();

  showDialogAlert: boolean = false;
  showDialogBreathSession: boolean = false;

  showSynopticTable: boolean = false;
  showAccelerometrSignals: boolean = false;
  showBreathSignals: boolean = false;
  showHearthSignals: boolean = false;

  endDate: Date = new Date();
  startDate: Date = new Date();

  constructor() {
    const navigazione = this.routerService.currentNavigation();

    this.changeLimits();
    
    // Recuperiamo il valore dallo stato (se esiste)
    if (navigazione?.extras.state) {
      this.id = navigazione.extras.state['id'];
      this.selectedId = this.id;
    }
  }

  ngOnDestroy() {
    this.dtTrigger.unsubscribe();

    if (this.langSubscription) {
      this.langSubscription.unsubscribe();
    }
  }

  ngOnInit(): void {
    this.tabsId = '0';

    this.langSubscription = this.translate.stream(['1d', '7d', '30d', 'range']).subscribe(translations => {
    this.timeOptions = [
        { label: translations['1d'], value: 1 },
        { label: translations['7d'], value: 2 },
        { label: translations['30d'], value: 3 },
        { label: translations['range'], value: 4 }
      ];
    });

    this.loadData();
  }

  confirmCustomRange(popover: any) {
    if (this.customDateRange && this.customDateRange[0] && this.customDateRange[1]) {
      popover.hide();
      this.loadMetrics();
    }
  }

  getAlertValue(alert : Alert) : string {
    const percent : number[] = [3, 4, 7, 8, 11, 12, 15, 16];
    const heart: number[] = [1, 2, 5, 6]
    const breath: number[] = [9, 10, 13, 14]

    const linguaBrowser: string = this.translate.currentLang() || this.translate.fallbackLang() || 'en';
    var v: string = '';
    var u: string = '';

    if (percent.includes(alert.type)) {
      v = new Intl.NumberFormat(linguaBrowser, {
        style: 'percent',
        minimumFractionDigits: 0, maximumFractionDigits: 0
      }).format(alert.value);
    } else if (heart.includes(alert.type)) {
      v = new Intl.NumberFormat(linguaBrowser, {
        maximumFractionDigits: 0
      }).format(alert.value);

      u = "[bpm]";
    } else if (breath.includes(alert.type)) {
      v = new Intl.NumberFormat(linguaBrowser, {
        maximumFractionDigits: 0
      }).format(alert.value);

      u = "[rpm]";
    } else {
      return new Intl.NumberFormat(linguaBrowser, {
        maximumFractionDigits: 0
      }).format(alert.value);
    }      

    return `${v} ${u}`
  }

  onAlertDialogClosed(alert: Alert) {
    this.alerts = this.alerts.map(item => {
      // Verifica se l'oggetto corrente corrisponde per id, timestamp e type
      if (
        item.userId === alert.userId &&
        item.timeStamp === alert.timeStamp &&
        item.type === alert.type
      ) {
        // Ritorna un nuovo oggetto che unisce lo stato precedente con le modifiche
        return { ...item, ...alert };
      }
      // Altrimenti ritorna l'oggetto invariato
      return item;
    });

    this.selectedAlert = Reset.alert();
  }

  onAlertEdit(alert: Alert) {
    this.selectedAlert = alert;
    this.showDialogAlert = true;
  }

  onBreathSessionDialogClosed(session: BreathSession) {
    this.selectedBreathSession = Reset.breathSession();
  }

  onCreateReport(popover: any) {
    const timestamp = new Date(this.startDate).getTime();
    const currentLang: string | null = this.translate.currentLang();
    const timeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone;
    this.apiService.getPatientReport(this.id, this.startDate, this.endDate, currentLang ?? 'en', timeZone). subscribe({
      next: (blob: Blob) => {
        const fileBlob = new Blob([blob], { type: 'application/pdf' });
        const fileURL = URL.createObjectURL(fileBlob);
        
        // Apre in una nuova scheda
        window.open(fileURL, '_blank');

        this.apiService.getPatientReports(this.id).subscribe({
          next: (r: PatientReport[]) => {
            this.reports = r;
          },
          error: (err: HttpErrorResponse) => {
            this.messageService.showError(err);
          }
        });
      },
      error: (err: HttpErrorResponse) => {
        this.messageService.showError(err);
      }
    });
    popover.hide();
  }

  onShowAccelerometerSignals() {
    this.showAccelerometrSignals = true;
  }

  onShowBreathSession(session: BreathSession) {
    this.selectedBreathSession = session;
    this.showDialogBreathSession = true;
  }

  onShowBreathSignals() {
    this.showBreathSignals = true;
  }

  onShowHearthSignals() {
    this.showHearthSignals = true;
  }

  onShowReport(report: PatientReport){
    const timestamp = new Date(report.timeStamp).getTime();
    const timestampEnd = new Date(report.timeStampEnd).getTime();
    this.apiService.getPatientDocument(report.userId, timestamp, timestampEnd, report.reportType). subscribe({
      next: (blob: Blob) => {
        const fileBlob = new Blob([blob], { type: 'application/pdf' });
        const fileURL = URL.createObjectURL(fileBlob);
        
        // Apre in una nuova scheda
        window.open(fileURL, '_blank');
      },
      error: (err: HttpErrorResponse) => {
        this.messageService.showError(err);
      }
    });
  }

  onShowSynopticTable() {
    this.showSynopticTable = true;
  }

  onTimeTypeChange(event: any, targetEvent: any, popover: any) {
    if (event.value === 4) {
      // Mostra il popover ancorato al SelectButton
      popover.show(event.originalEvent, targetEvent);
    } else {
      popover.hide();
      this.customDateRange = undefined; // Resetta se si sceglie un altro filtro veloce
      this.loadMetrics();
    }
  }

  onZoomChanged(scales: any) {
    this.zoomLevel.next(scales);
  }

  onZoomCommand(event: number) {
    this.zoomCommand.next(event)
  }

  private changeLimits() {
    const now = new Date();
    const midnightUtc = Date.UTC(
          now.getUTCFullYear(),
          now.getUTCMonth(),
      now.getUTCDate()
    );

    switch (this.selectedTime) {
      case 1:
        this.min = midnightUtc;
        this.max = new Date(this.max + 24 * 60 * 60 * 1000).getTime();
        break;

      case 2:
        this.max = midnightUtc;
        this.min = new Date(this.max - 7 * 24 * 60 * 60 * 1000).getTime();
        break;

      case 3:
        this.max = midnightUtc;
        const utcMinus1Month = new Date(midnightUtc);
        utcMinus1Month.setUTCMonth(utcMinus1Month.getUTCMonth() - 1);
        this.min = utcMinus1Month.getTime();
        break;

      case 4:
        if (this.customDateRange != undefined) {
          this.min = this.customDateRange[0].getTime();
          this.max = this.customDateRange[1].getTime();
        }
        break;

      default:
        break;
    }
  }

  private loadMetrics() {
    this.loadingMetrics = true;

    var start: string = '';
    var end: string = '';
    if (this.selectedTime === 4 && this.customDateRange != undefined) {
      start = this.customDateRange[0].toISOString();
      end = this.customDateRange[1].toISOString();
    }

    this.apiService.getPatientGraphs(this.id, this.selectedTime, start, end).subscribe({
      next: (data) => {
        this.changeLimits();

        this.patient.fcGraphs = data.slice(0, 2);
        this.patient.frGraphs = data.slice(2, 4);
        this.dtTrigger.next(true);
      },
      error: (err: HttpErrorResponse) => {
        this.loadingMetrics = false;
        this.messageService.showError(err);
      },
      complete: () => {
        this.loadingMetrics = false;
      }
    })
  }

  private async loadData() : Promise<void> {
    if (this.id === '') {
      this.messageService.showErrorMessage('Identificativo paziente NON valido!');
      return;
    }

    this.loading = true;

    try {
      const [patientData, alertsData, reportsData] = await Promise.all([
        firstValueFrom(this.apiService.getPatientSummary(this.id)),
        firstValueFrom(this.apiService.getPatientAlerts(this.id)),
        firstValueFrom(this.apiService.getPatientReports(this.id))
      ]);

      this.patient = patientData;
      this.alertsToAnalyse = this.patient.alert.length;
      this.alerts = alertsData;
      this.reports = reportsData;

      this.endDate = new Date(this.patient.lastMeasure);
      this.startDate = new Date(this.patient.lastMeasure);
      this.startDate.setDate(this.startDate.getDate() - 2);

      if (Parameters.loggedUser.features.breath) {
        this.breathSessions = await firstValueFrom(this.apiService.getPatientBreathSessions(this.id))
      } else {
        this.breathSessions = []
      }

      this.dtTrigger.next(true);
      this.loading = false;
    } catch (error: any) {
      this.loading = false;
      if (error instanceof HttpErrorResponse) {
        this.messageService.showError(error);
      } else {
        const err = error as Error;
        this.messageService.showErrorMessage(err.message);
      }
    }
  }
}
